// services/s3Upload.js
//
// Direct-to-S3 uploads for large report media.
//
// WHY: Previously every media file traveled browser -> ALB -> nginx -> Daphne
// -> S3, so a 250MB video tied up the whole backend chain for minutes and hit
// gateway timeouts (502/504). With this flow the backend only signs a
// short-lived, size-capped "permission slip" (a presigned S3 POST); the
// browser then uploads the bytes STRAIGHT to S3. The backend never sees the
// file — uploads scale regardless of instance size.
//
// FLOW (3 small requests instead of 1 giant one):
//   1. POST /api/reports/presign-upload/  -> presigned POST per file
//   2. Browser uploads each file directly to S3 (with real progress events)
//   3. POST /api/reports/{id}/attach-media/ with the S3 keys
//
// The 500MB max size and allowed content types are enforced INSIDE the signed
// S3 policy server-side — a tampered client cannot exceed them.

import axiosInstance from '../api/axiosInstance';

// Thrown when the backend doesn't have the presign endpoints yet (older
// deploy). Callers catch this and fall back to the legacy multipart path.
export class PresignUnavailableError extends Error {
  constructor() {
    super('Presigned upload endpoint unavailable');
    this.name = 'PresignUnavailableError';
  }
}

/**
 * Upload files directly to S3 via presigned POSTs.
 *
 * @param {File[]} files
 * @param {(fileName: string, percent: number) => void} [onProgress]
 *        Called with (file.name, 0-100) as each file uploads.
 * @returns {Promise<string[]>} S3 keys, same order as `files`.
 */
export async function uploadFilesDirectToS3(files, onProgress) {
  if (!files || files.length === 0) return [];

  // 1. Ask our backend to presign each file
  let presignResponse;
  try {
    presignResponse = await axiosInstance.post('/api/reports/presign-upload/', {
      files: files.map((f) => ({
        filename: f.name,
        content_type: f.type || 'application/octet-stream',
        size: f.size,
      })),
    });
  } catch (error) {
    if (error.response?.status === 404) {
      throw new PresignUnavailableError();
    }
    // Meaningful backend validation errors (too big, bad type, PA user)
    const detail = error.response?.data?.detail;
    throw new Error(detail || 'Could not prepare upload. Please try again.');
  }

  const uploads = presignResponse.data.uploads;

  // 2. Upload each file straight to S3.
  //    XMLHttpRequest instead of fetch so we get upload progress events.
  const keys = [];
  for (let i = 0; i < uploads.length; i++) {
    const { upload, key } = uploads[i];
    const file = files[i];

    await new Promise((resolve, reject) => {
      const form = new FormData();
      // S3 requires the policy fields first and the file LAST.
      Object.entries(upload.fields).forEach(([k, v]) => form.append(k, v));
      form.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', upload.url);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(file.name, Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        // S3 presigned POST success is 204 (or 201 with success_action_status)
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(file.name, 100);
          resolve();
        } else {
          reject(new Error(`S3 rejected "${file.name}" (HTTP ${xhr.status})`));
        }
      };
      xhr.onerror = () =>
        reject(new Error(`Network error uploading "${file.name}". Check your connection and try again.`));
      xhr.onabort = () => reject(new Error(`Upload of "${file.name}" was cancelled.`));

      xhr.send(form);
    });

    keys.push(key);
  }

  return keys;
}

/**
 * Attach already-uploaded S3 objects to a report.
 * The backend verifies each key actually exists in S3 before creating rows.
 *
 * @param {number|string} reportId
 * @param {string[]} keys
 */
export async function attachMediaToReport(reportId, keys) {
  if (!keys || keys.length === 0) return { attached: [] };
  const { data } = await axiosInstance.post(
    `/api/reports/${reportId}/attach-media/`,
    { keys }
  );
  if (data.errors && data.errors.length > 0) {
    console.warn('Some media could not be attached:', data.errors);
  }
  return data;
}
